import type { Role } from '@/types';
import { ic, sized, type IconElement } from '@/components/Icons';
import { useI18n } from '@/i18n';
import { useAppStore } from '@/store/useAppStore';
import { useGo, useRoute, type ScreenId } from '@/hooks/useRoute';

// Todos los roles con acceso al portal. SITE_ADMIN faltaba, y sin él la
// navegación se le quedaba vacía en cuanto dejó de disfrazarse de SITE_MGR.
const ALL: Role[] = ['ETC_ADMIN', 'PARTNER_MGR', 'SITE_MGR', 'SITE_ADMIN'];

const SHORT: Record<string, { es: string; en: string }> = {
  dashboard:   { es: 'Inicio',    en: 'Home' },
  org:         { es: 'Orgs',      en: 'Orgs' },
  sites:       { es: 'Sites',     en: 'Sites' },
  instructors: { es: 'Instruct.', en: 'Instruct.' },
  users:       { es: 'Usuarios',  en: 'Users' },
  reports:     { es: 'Reportes',  en: 'Reports' },
  admin:       { es: 'Admin',     en: 'Admin' },
};

const ITEMS: { id: ScreenId; icon: IconElement; roles: Role[] }[] = [
  { id: 'dashboard',   icon: ic.dashboard, roles: ALL },
  { id: 'org',         icon: ic.org,       roles: ALL },
  { id: 'sites',       icon: ic.site,      roles: ALL },
  { id: 'instructors', icon: ic.instr,     roles: ALL },
  { id: 'users',       icon: ic.users,     roles: ['ETC_ADMIN'] },
  { id: 'reports',     icon: ic.reports,   roles: ['ETC_ADMIN'] },
  { id: 'admin',       icon: ic.admin,     roles: ['ETC_ADMIN'] },
];

/** Mobile tab bar. Detail screens highlight their parent tab. */
export function BottomNav() {
  const { lang, t } = useI18n();
  const go = useGo();
  const route = useRoute();
  const role = useAppStore((s) => s.role);

  const items = ITEMS.filter((i) => i.roles.includes(role));
  const ACTIVE_MAP: Record<string, string> = { site: 'sites', instructorDetail: 'instructors' };
  const active = ACTIVE_MAP[route.screen] || route.screen;

  return (
    <nav
      aria-label={lang === 'es' ? 'Navegación principal' : 'Main navigation'}
      style={{
        position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 60,
        display: 'flex', alignItems: 'stretch',
        background: 'linear-gradient(162deg, #1B2330 0%, #2A3441 100%)',
        borderTop: '1px solid rgba(255,255,255,0.14)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        overflowX: 'auto',
      }}
    >
      {items.map((it) => {
        const isActive = active === it.id;
        return (
          <button
            key={it.id}
            onClick={() => go(it.id)}
            aria-current={isActive ? 'page' : undefined}
            style={{
              flex: '1 0 0', minWidth: 52, border: 'none', background: 'transparent', cursor: 'pointer',
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
              padding: '8px 2px 7px', fontFamily: 'inherit',
              color: isActive ? '#FBB089' : 'rgba(255,255,255,0.62)',
              borderTop: isActive ? '2px solid var(--brand-primary)' : '2px solid transparent',
            }}
          >
            {sized(it.icon, 19)}
            <span style={{ fontSize: 9.5, fontWeight: isActive ? 700 : 600, letterSpacing: '.01em', whiteSpace: 'nowrap' }}>
              {SHORT[it.id]?.[lang] || t('nav.' + it.id)}
            </span>
          </button>
        );
      })}
    </nav>
  );
}
